import React, { useRef } from "react";
import { ImageOff, Upload, Trash2, Copy, Info, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { AdminImage } from "./adminSections";
import { useResourceImage } from "../../lib/useResourceImage";
import { formatBytes, formatDate } from "../../lib/imageStore";

interface ImageFieldAdminProps {
  image: AdminImage;
}

// Full image-override control: FileReader -> base64 Data URL -> localStorage,
// so the preview survives a reload and is picked up by the public landing
// wherever this resourceKey is used. Nothing is uploaded anywhere.
export const ImageFieldAdmin: React.FC<ImageFieldAdminProps> = ({ image }) => {
  const { src, meta, isOverridden, selectFile, clear } = useResourceImage(image.key, image.src);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const result = await selectFile(file);
    if (!result.ok) {
      toast.error(result.error || "No se pudo cargar la imagen");
    }
    if (inputRef.current) inputRef.current.value = "";
  };

  const handleRemovePreview = () => {
    clear();
    toast.success("Vista previa local eliminada");
  };

  const handleCopyPath = async () => {
    try {
      await navigator.clipboard.writeText(image.path);
      toast.success("Ruta copiada");
    } catch {
      toast.error("No se pudo copiar la ruta");
    }
  };

  return (
    <div
      data-testid={`admin-image-${image.key}`}
      className="grid sm:grid-cols-[120px_1fr] gap-4 rounded-xl border border-neutral-200 p-4"
    >
      <div className="h-24 w-full sm:w-[120px] rounded-lg bg-motor-mist border border-neutral-200 flex items-center justify-center overflow-hidden">
        {src ? (
          <img src={src} alt={image.alt} className="h-full w-full object-cover" />
        ) : (
          <ImageOff className="h-6 w-6 text-neutral-400" />
        )}
      </div>

      <div className="grid gap-1.5 text-sm">
        <div className="flex flex-wrap items-center gap-2">
          <p className="font-semibold text-motor-ink">{image.label}</p>
          {isOverridden && (
            <span
              data-testid={`admin-image-override-badge-${image.key}`}
              className="inline-flex items-center gap-1 rounded-full bg-emerald-50 border border-emerald-200 px-2 py-0.5 text-[11px] font-semibold text-emerald-700"
            >
              <CheckCircle2 className="h-3 w-3" /> Vista previa local activa
            </span>
          )}
        </div>

        <p className="text-xs font-mono text-neutral-500 break-all">Ruta real: {image.path}</p>

        {isOverridden && meta ? (
          <div data-testid={`admin-image-meta-${image.key}`} className="grid gap-0.5 text-xs text-neutral-500">
            <span>Archivo seleccionado: {meta.fileName}</span>
            <span>Tamaño: {formatBytes(meta.fileSize)}</span>
            <span>Seleccionado: {formatDate(meta.selectedAt)}</span>
          </div>
        ) : (
          <div className="flex flex-wrap gap-3 text-xs text-neutral-500">
            <span>Dimensiones: {image.recommendedSize}</span>
            <span>Formato: {image.recommendedFormat}</span>
            <span>Alt actual: “{image.alt}”</span>
          </div>
        )}

        <div className="mt-1 flex flex-wrap gap-2">
          <label
            data-testid={`admin-image-upload-${image.key}`}
            className="inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-neutral-300 px-3 py-1.5 text-xs font-semibold text-motor-ink hover:border-motor-red hover:text-motor-red transition-colors"
          >
            <Upload className="h-3.5 w-3.5" />
            Previsualizar imagen local
            <input
              ref={inputRef}
              type="file"
              accept="image/png,image/jpeg,image/jpg,image/webp"
              onChange={handleFileSelect}
              className="hidden"
            />
          </label>

          {isOverridden && (
            <button
              data-testid={`admin-image-remove-${image.key}`}
              onClick={handleRemovePreview}
              className="inline-flex items-center gap-1.5 rounded-full border border-neutral-300 px-3 py-1.5 text-xs font-semibold text-motor-red hover:bg-motor-red hover:text-white transition-colors"
            >
              <Trash2 className="h-3.5 w-3.5" /> Eliminar vista previa local
            </button>
          )}

          <button
            data-testid={`admin-image-copy-path-${image.key}`}
            onClick={handleCopyPath}
            className="inline-flex items-center gap-1.5 rounded-full border border-neutral-300 px-3 py-1.5 text-xs font-semibold text-motor-ink hover:border-motor-red hover:text-motor-red transition-colors"
          >
            <Copy className="h-3.5 w-3.5" /> Copiar ruta del archivo
          </button>
        </div>

        <p className="mt-2 flex items-start gap-2 rounded-lg bg-motor-mist border border-neutral-200 px-3 py-2 text-[11px] text-neutral-600">
          <Info className="h-3.5 w-3.5 shrink-0 mt-0.5 text-motor-red" />
          Esta imagen solo está guardada en tu navegador. Para publicarla permanentemente,
          reemplaza el archivo indicado en el proyecto (
          <span className="font-mono">{image.path}</span>) y realiza un nuevo despliegue.
        </p>
      </div>
    </div>
  );
};
